// Plan 20-06 — META-05 web-side mirror of the backend ACHIEVEMENT_LIST.
//
// DEC-018 single-source-of-truth note: the backend module
// (apps/backend/src/match/achievements/achievement-list.ts) is the runtime
// contract for trigger logic; this web module is the render-side mirror for
// icon hint + rarity + i18n key dispatch. The list of IDs MUST match the
// backend 1:1 — `achievement-meta.test.ts` reads the backend source as text
// and asserts the id sets are equal (see Test 2 in that file).
//
// We mirror only what the UI needs to render a badge: id, rarity, the two
// i18n keys, and a placeholder icon hint (CSS class). Trigger logic, params,
// and the closed AchievementTriggerKind enum stay backend-private — the
// player does NOT see "play a 12+ letter word" via the wire (the description
// is i18n-rendered on the client and uses the authored Docs/Achievements.md
// copy, not the trigger expression).
//
// Source order matches `Docs/Achievements.md` rows 1-30 (the backend literal
// uses the same order). Snake_case ids are SQLite PRIMARY KEY safe per the
// Plan 20-01 invariant test 7.

export type AchievementRarity = 'common' | 'rare' | 'epic' | 'legendary'

export interface AchievementMeta {
  /** snake_case id, matches backend ACHIEVEMENT_LIST[*].id 1:1. */
  readonly id: string
  readonly rarity: AchievementRarity
  /** i18n key for display name. Always `achievements.<id>.name`. */
  readonly i18n_key_name: string
  /** i18n key for description. Always `achievements.<id>.description`. */
  readonly i18n_key_description: string
  /** CSS class hint for the placeholder badge glyph (real art deferred). */
  readonly icon_hint: string
}

/**
 * The 30 web-side achievement render-meta entries. Mirrors the backend
 * ACHIEVEMENT_LIST 1:1 in id + rarity + i18n keys (test asserts).
 */
export const ACHIEVEMENTS_META: ReadonlyArray<AchievementMeta> = [
  {
    id: 'sesquipedalian',
    rarity: 'common',
    i18n_key_name: 'achievements.sesquipedalian.name',
    i18n_key_description: 'achievements.sesquipedalian.description',
    icon_hint: 'icon-word',
  },
  {
    id: 'knights_tale',
    rarity: 'rare',
    i18n_key_name: 'achievements.knights_tale.name',
    i18n_key_description: 'achievements.knights_tale.description',
    icon_hint: 'icon-knight',
  },
  {
    id: 'holdem_soul',
    rarity: 'common',
    i18n_key_name: 'achievements.holdem_soul.name',
    i18n_key_description: 'achievements.holdem_soul.description',
    icon_hint: 'icon-reveal',
  },
  {
    id: 'mind_the_bank',
    rarity: 'common',
    i18n_key_name: 'achievements.mind_the_bank.name',
    i18n_key_description: 'achievements.mind_the_bank.description',
    icon_hint: 'icon-token',
  },
  {
    id: 'the_long_game',
    rarity: 'epic',
    i18n_key_name: 'achievements.the_long_game.name',
    i18n_key_description: 'achievements.the_long_game.description',
    icon_hint: 'icon-clock',
  },
  {
    id: 'pawn_whisperer',
    rarity: 'common',
    i18n_key_name: 'achievements.pawn_whisperer.name',
    i18n_key_description: 'achievements.pawn_whisperer.description',
    icon_hint: 'icon-pawn',
  },
  {
    id: 'friend_of_the_court',
    rarity: 'rare',
    i18n_key_name: 'achievements.friend_of_the_court.name',
    i18n_key_description: 'achievements.friend_of_the_court.description',
    icon_hint: 'icon-tournament',
  },
  {
    id: 'comeback_kid',
    rarity: 'common',
    i18n_key_name: 'achievements.comeback_kid.name',
    i18n_key_description: 'achievements.comeback_kid.description',
    icon_hint: 'icon-comeback',
  },
  {
    id: 'bishops_promise',
    rarity: 'common',
    i18n_key_name: 'achievements.bishops_promise.name',
    i18n_key_description: 'achievements.bishops_promise.description',
    icon_hint: 'icon-bishop',
  },
  {
    id: 'kings_endgame',
    rarity: 'rare',
    i18n_key_name: 'achievements.kings_endgame.name',
    i18n_key_description: 'achievements.kings_endgame.description',
    icon_hint: 'icon-king',
  },
  {
    id: 'glasses_half_full',
    rarity: 'common',
    i18n_key_name: 'achievements.glasses_half_full.name',
    i18n_key_description: 'achievements.glasses_half_full.description',
    icon_hint: 'icon-powerup',
  },
  {
    id: 'castle_builder',
    rarity: 'common',
    i18n_key_name: 'achievements.castle_builder.name',
    i18n_key_description: 'achievements.castle_builder.description',
    icon_hint: 'icon-castle',
  },
  {
    id: 'phonetic_phantom',
    rarity: 'common',
    i18n_key_name: 'achievements.phonetic_phantom.name',
    i18n_key_description: 'achievements.phonetic_phantom.description',
    icon_hint: 'icon-vowels',
  },
  {
    id: 'q_without_u',
    rarity: 'legendary',
    i18n_key_name: 'achievements.q_without_u.name',
    i18n_key_description: 'achievements.q_without_u.description',
    icon_hint: 'icon-q',
  },
  {
    id: 'triple_reveal',
    rarity: 'common',
    i18n_key_name: 'achievements.triple_reveal.name',
    i18n_key_description: 'achievements.triple_reveal.description',
    icon_hint: 'icon-triple',
  },
  {
    id: 'anagram_sigil_master',
    rarity: 'common',
    i18n_key_name: 'achievements.anagram_sigil_master.name',
    i18n_key_description: 'achievements.anagram_sigil_master.description',
    icon_hint: 'icon-sigil',
  },
  {
    id: 'whisperers_promise_kept',
    rarity: 'rare',
    i18n_key_name: 'achievements.whisperers_promise_kept.name',
    i18n_key_description: 'achievements.whisperers_promise_kept.description',
    icon_hint: 'icon-whisper',
  },
  {
    id: 'the_vanishing_act',
    rarity: 'common',
    i18n_key_name: 'achievements.the_vanishing_act.name',
    i18n_key_description: 'achievements.the_vanishing_act.description',
    icon_hint: 'icon-vanishing',
  },
  {
    id: 'tongue_tie_twins',
    rarity: 'common',
    i18n_key_name: 'achievements.tongue_tie_twins.name',
    i18n_key_description: 'achievements.tongue_tie_twins.description',
    icon_hint: 'icon-tongue-tie',
  },
  {
    id: 'first_blood',
    rarity: 'common',
    i18n_key_name: 'achievements.first_blood.name',
    i18n_key_description: 'achievements.first_blood.description',
    icon_hint: 'icon-first',
  },
  {
    id: 'top_of_the_court',
    rarity: 'epic',
    i18n_key_name: 'achievements.top_of_the_court.name',
    i18n_key_description: 'achievements.top_of_the_court.description',
    icon_hint: 'icon-ladder',
  },
  {
    id: 'marathon_runner',
    rarity: 'common',
    i18n_key_name: 'achievements.marathon_runner.name',
    i18n_key_description: 'achievements.marathon_runner.description',
    icon_hint: 'icon-marathon',
  },
  {
    id: 'veteran',
    rarity: 'legendary',
    i18n_key_name: 'achievements.veteran.name',
    i18n_key_description: 'achievements.veteran.description',
    icon_hint: 'icon-veteran',
  },
  {
    id: 'both_hands',
    rarity: 'rare',
    i18n_key_name: 'achievements.both_hands.name',
    i18n_key_description: 'achievements.both_hands.description',
    icon_hint: 'icon-languages',
  },
  {
    id: 'cycle_champion',
    rarity: 'rare',
    i18n_key_name: 'achievements.cycle_champion.name',
    i18n_key_description: 'achievements.cycle_champion.description',
    icon_hint: 'icon-cycle',
  },
  {
    id: 'three_cycle_streak',
    rarity: 'epic',
    i18n_key_name: 'achievements.three_cycle_streak.name',
    i18n_key_description: 'achievements.three_cycle_streak.description',
    icon_hint: 'icon-streak',
  },
  {
    id: 'bot_whisperer',
    rarity: 'common',
    i18n_key_name: 'achievements.bot_whisperer.name',
    i18n_key_description: 'achievements.bot_whisperer.description',
    icon_hint: 'icon-bot',
  },
  {
    id: 'daily_devotion',
    rarity: 'common',
    i18n_key_name: 'achievements.daily_devotion.name',
    i18n_key_description: 'achievements.daily_devotion.description',
    icon_hint: 'icon-daily',
  },
  {
    id: 'monthly_devotion',
    rarity: 'rare',
    i18n_key_name: 'achievements.monthly_devotion.name',
    i18n_key_description: 'achievements.monthly_devotion.description',
    icon_hint: 'icon-monthly',
  },
  {
    id: 'the_sesquipedalian_sigil',
    rarity: 'epic',
    i18n_key_name: 'achievements.the_sesquipedalian_sigil.name',
    i18n_key_description: 'achievements.the_sesquipedalian_sigil.description',
    icon_hint: 'icon-sigil-word',
  },
] as const

/**
 * O(1) lookup map. Consumed by `AchievementsGrid` when projecting backend
 * achievement_id strings into renderable meta. Unknown ids resolve to
 * `undefined` — the grid skips them safely (T-20-39 mitigation).
 */
export const ACHIEVEMENTS_BY_ID: ReadonlyMap<string, AchievementMeta> = new Map(
  ACHIEVEMENTS_META.map((a) => [a.id, a]),
)
